import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtINR, fmtDT } from "@/lib/api";
import { ShieldCheck, Package, Users, TrendingUp } from "lucide-react";

/**
 * PMJAY tracking dashboard: total beneficiaries, OPD/IPD counts by scheme,
 * package amount, and recent PMJAY / PACKAGE cases with drill-down links.
 */
export default function PMJAY() {
  const [dash, setDash] = useState(null);
  const [pkgs, setPkgs] = useState([]);
  const [tab, setTab] = useState("opd");
  useEffect(() => {
    api.get("/pmjay/dashboard").then((r) => setDash(r.data));
    api.get("/pmjay-packages").then((r) => setPkgs(r.data));
  }, []);

  if (!dash) return <div>Loading...</div>;

  const totalOpd = dash.opd.PMJAY + dash.opd.PACKAGE;
  const totalIpd = dash.ipd.PMJAY + dash.ipd.PACKAGE;
  const totalPackageValue = dash.opd.package_amount + dash.ipd.package_amount;

  const KPI = ({ icon: Ic, label, val, sub, tone = "blue" }) => (
    <div className="bg-white border p-4">
      <div className="flex justify-between">
        <div>
          <div className="label-tiny">{label}</div>
          <div className="font-display text-2xl font-semibold mt-2 data-mono">{val}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-9 h-9 flex items-center justify-center bg-${tone}-50 text-${tone}-700`}><Ic className="w-5 h-5"/></div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-semibold">PMJAY Tracking Dashboard</h1>
        <p className="text-sm text-slate-500">Beneficiaries, package cases and utilisation under Pradhan Mantri Jan Arogya Yojana.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={Users} label="Total Beneficiaries" val={dash.total_beneficiaries} tone="teal" />
        <KPI icon={ShieldCheck} label="OPD (PMJAY + Package)" val={totalOpd} sub={`PMJAY ${dash.opd.PMJAY} · Package ${dash.opd.PACKAGE}`} tone="blue" />
        <KPI icon={ShieldCheck} label="IPD (PMJAY + Package)" val={totalIpd} sub={`PMJAY ${dash.ipd.PMJAY} · Package ${dash.ipd.PACKAGE}`} tone="amber" />
        <KPI icon={TrendingUp} label="Package Value Booked" val={fmtINR(totalPackageValue)} sub={`OPD ${fmtINR(dash.opd.package_amount)} + IPD ${fmtINR(dash.ipd.package_amount)}`} tone="red" />
      </div>

      <div className="bg-white border">
        <div className="flex border-b">
          {[["opd", "OPD Cases", dash.recent_opd.length], ["ipd", "IPD Cases", dash.recent_ipd.length], ["packages", "Package Master", pkgs.length]].map(([k, l, c]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`pmjay-tab-${k}`}
              className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === k ? "border-blue-700 text-blue-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {l} <span className="ml-1 text-xs bg-slate-100 px-1.5 rounded-sm">{c}</span>
            </button>
          ))}
        </div>

        {tab === "opd" && (
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Date</th><th className="label-tiny">OPD No</th><th className="label-tiny">Patient</th><th className="label-tiny">UHID</th><th className="label-tiny">Doctor</th><th className="label-tiny">Department</th><th className="label-tiny">Scheme</th><th className="label-tiny">Package</th><th className="label-tiny text-right">Amount</th></tr></thead>
            <tbody>
              {dash.recent_opd.map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="p-2 text-xs">{fmtDT(r.created_at)}</td>
                  <td className="font-mono text-xs">{r.opd_no}</td>
                  <td><Link to={`/opd/${r.id}`} className="text-blue-700 hover:underline">{r.patient_name}</Link></td>
                  <td className="font-mono text-xs">{r.uhid}</td>
                  <td>{r.doctor_name}</td>
                  <td>{r.department}</td>
                  <td><span className="text-xs font-semibold text-teal-700">{r.scheme || "PMJAY"}</span></td>
                  <td className="text-xs">{r.pmjay_package_name || "-"}</td>
                  <td className="text-right font-mono">{r.pmjay_package_amount ? fmtINR(r.pmjay_package_amount) : "-"}</td>
                </tr>
              ))}
              {dash.recent_opd.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-500">No PMJAY OPD cases yet</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "ipd" && (
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Admitted</th><th className="label-tiny">IPD No</th><th className="label-tiny">Patient</th><th className="label-tiny">UHID</th><th className="label-tiny">Doctor</th><th className="label-tiny">Department</th><th className="label-tiny">Scheme</th><th className="label-tiny">Package</th><th className="label-tiny text-right">Amount</th></tr></thead>
            <tbody>
              {dash.recent_ipd.map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="p-2 text-xs">{fmtDT(r.admission_date)}</td>
                  <td className="font-mono text-xs">{r.ipd_no}</td>
                  <td><Link to={`/ipd/${r.id}`} className="text-blue-700 hover:underline">{r.patient_name}</Link></td>
                  <td className="font-mono text-xs">{r.uhid}</td>
                  <td>{r.doctor_name}</td>
                  <td>{r.department}</td>
                  <td><span className="text-xs font-semibold text-teal-700">{r.scheme || "PMJAY"}</span></td>
                  <td className="text-xs">{r.pmjay_package_name || "-"}</td>
                  <td className="text-right font-mono">{r.pmjay_package_amount ? fmtINR(r.pmjay_package_amount) : "-"}</td>
                </tr>
              ))}
              {dash.recent_ipd.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-500">No PMJAY IPD cases yet</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "packages" && (
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Code</th><th className="label-tiny">Package</th><th className="label-tiny">Department</th><th className="label-tiny text-right">Amount</th></tr></thead>
            <tbody>{pkgs.map((p) => <tr key={p.id} className="border-b"><td className="p-2 font-mono">{p.code}</td><td>{p.name}</td><td>{p.department}</td><td className="text-right font-mono">{fmtINR(p.amount)}</td></tr>)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
